/**
 * สลับเวรกันเอง (มุมพนักงาน) — สร้างคำขอ → คู่สลับยอมรับ → หัวหน้าอนุมัติ
 *   1. สร้างคำขอ: เลือกวัน + เลือกเพื่อน + เหตุผล
 *   2. รอฉันตอบรับ: คำขอที่เพื่อนขอสลับกับเรา → ยอมรับ/ปฏิเสธ
 *   3. คำขอของฉัน: ติดตามสถานะ
 * ดู docs/SHIFT_ROSTER_BLUEPRINT.md
 */
import { useState } from 'react';
import { ArrowLeftRight, Check, X, Send } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { useMySwaps, useRespondSwap, useCoworkers, useCreateSwap, type SwapRow } from '../hooks';
import { useToast } from '../components/ui/toast';
import { useAuthStore } from '../lib/store';

const STATUS: Record<SwapRow['status'], { label: string; cls: string }> = {
  awaiting_target: { label: 'รอเพื่อนตอบรับ', cls: 'badge-warn' },
  pending: { label: 'รอหัวหน้าอนุมัติ', cls: 'badge-warn' },
  approved: { label: 'อนุมัติแล้ว', cls: 'badge-success' },
  rejected: { label: 'หัวหน้าไม่อนุมัติ', cls: 'badge-danger' },
  declined: { label: 'เพื่อนปฏิเสธ', cls: 'badge-danger' },
  cancelled: { label: 'ยกเลิก', cls: 'badge-danger' },
};

export function Swap() {
  const me = useAuthStore((s) => s.profile?.emp_code ?? '');
  const coworkers = useCoworkers();
  const mine = useMySwaps();
  const create = useCreateSwap();
  const respond = useRespondSwap();
  const toast = useToast();

  const [date, setDate] = useState('');
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');

  const nameOf = (code: string) => coworkers.data?.find((c) => c.emp_code === code)?.name || code;
  const rows = mine.data ?? [];
  const toRespond = rows.filter((r) => r.target_emp_code === me && r.status === 'awaiting_target');
  const myRequests = rows.filter((r) => r.requester_emp_code === me);
  const history = rows.filter((r) => r.target_emp_code === me && r.status !== 'awaiting_target');

  function submit() {
    if (!date || !target) return toast.error('เลือกวันและเพื่อนที่จะสลับ');
    create.mutate(
      { date, requester_emp_code: me, target_emp_code: target, reason: reason || undefined },
      {
        onSuccess: () => { toast.success('ส่งคำขอแล้ว — รอเพื่อนตอบรับ'); setDate(''); setTarget(''); setReason(''); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'ส่งไม่สำเร็จ'),
      },
    );
  }

  function doRespond(id: string, accept: boolean) {
    respond.mutate({ id, accept }, {
      onSuccess: () => toast.success(accept ? 'ยอมรับแล้ว — ส่งให้หัวหน้าอนุมัติ' : 'ปฏิเสธแล้ว'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ทำรายการไม่สำเร็จ'),
    });
  }

  return (
    <PageLayout title="สลับเวร">
      <div className="space-y-5 pb-10">
        {/* สร้างคำขอ */}
        <div className="card p-4 space-y-3">
          <div className="font-medium text-gray-900 flex items-center gap-1.5"><ArrowLeftRight className="w-4 h-4 text-teal" /> ขอสลับเวร</div>
          <div>
            <label className="label">วันที่ต้องการสลับ</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="label">สลับกับ</label>
            <select value={target} onChange={(e) => setTarget(e.target.value)} className="input w-full">
              <option value="">— เลือกเพื่อนร่วมงาน —</option>
              {(coworkers.data ?? []).map((c) => (
                <option key={c.emp_code} value={c.emp_code}>{c.name}{c.nickname ? ` (${c.nickname})` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">เหตุผล (ถ้ามี)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="เช่น ติดธุระ" className="input w-full" />
          </div>
          <button onClick={submit} disabled={create.isPending || !date || !target} className="btn-cta w-full justify-center flex items-center gap-2">
            <Send className="w-4 h-4" /> {create.isPending ? 'กำลังส่ง…' : 'ส่งคำขอ'}
          </button>
        </div>

        {/* รอฉันตอบรับ */}
        {toRespond.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">รอฉันตอบรับ ({toRespond.length})</div>
            {toRespond.map((r) => (
              <div key={r.id} className="card p-3 space-y-2 border-amber-200 bg-amber-50/40">
                <div className="text-sm text-gray-800">
                  <b>{nameOf(r.requester_emp_code)}</b> ขอสลับเวรกับคุณ วันที่ {r.date}
                  {r.reason && <span className="text-gray-500"> · {r.reason}</span>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => doRespond(r.id, true)} disabled={respond.isPending} className="btn-cta flex-1 justify-center flex items-center gap-1 text-sm">
                    <Check className="w-4 h-4" /> ยอมรับ
                  </button>
                  <button onClick={() => doRespond(r.id, false)} disabled={respond.isPending} className="btn-secondary flex-1 justify-center flex items-center gap-1 text-sm text-red-700 border-red-200">
                    <X className="w-4 h-4" /> ปฏิเสธ
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* คำขอของฉัน */}
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">คำขอของฉัน</div>
          {myRequests.length === 0 ? (
            <p className="text-sm text-gray-400">ยังไม่มีคำขอ</p>
          ) : myRequests.map((r) => (
            <div key={r.id} className="card p-3 flex items-center justify-between gap-2">
              <div className="text-sm text-gray-800">
                สลับกับ <b>{nameOf(r.target_emp_code)}</b> · {r.date}
                {r.reason && <span className="text-gray-500"> · {r.reason}</span>}
              </div>
              <span className={`${STATUS[r.status].cls} shrink-0`}>{STATUS[r.status].label}</span>
            </div>
          ))}
        </div>

        {/* ประวัติที่ฉันตอบไปแล้ว */}
        {history.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">เพื่อนเคยขอสลับกับฉัน</div>
            {history.map((r) => (
              <div key={r.id} className="card p-3 flex items-center justify-between gap-2">
                <div className="text-sm text-gray-800"><b>{nameOf(r.requester_emp_code)}</b> · {r.date}</div>
                <span className={`${STATUS[r.status].cls} shrink-0`}>{STATUS[r.status].label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
